import _ from 'lodash';
import * as graphql from '../../graphql';
import { t } from 'i18next';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { ContextSchema, StagedContext } from '@/vantage/types/Context.types';
import moment from 'moment';
import { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';
import { VantageNames } from '@/vantage/constants';

export interface ReasonCodesConfig {
  labelingWindowHours?: number;
}

export interface ReasonCode {
  prompt?: string;
  category?: string;
  label?: string;
  children?: ReasonCode[];
}

export interface SelectedCapsules {
  conditionId: string;
  datumId: string;
  duration: moment.Duration;
  reasonCodes: ReasonCode[];
}

type ReasonCodeWindows = Record<string, { start: Date; end: Date }>;

export const labels = (reasonCodes: ReasonCode[] | undefined): string[] | undefined => {
  if (!reasonCodes) {
    return;
  }
  return reasonCodes.map((code) => code.label).filter((label) => label) as string[];
};

export const stringLabel = (choices: string[] | null) =>
  choices && choices.length > 0 ? choices.join(' → ') : undefined;

export const chosenReasonCodes = (
  reasonCodes: ReasonCode[] | undefined,
  [choice, ...choices]: string[],
): ReasonCode[] => {
  if (choice) {
    const code = reasonCodes?.find((rc) => rc.label === choice);
    if (code) {
      return [code, ...chosenReasonCodes(code.children, choices)];
    }
  }
  return [];
};

export const chosenReasonCode = (reasonCodes: ReasonCode[], choices: string[]) => {
  const codes = chosenReasonCodes(reasonCodes, choices);
  return codes[codes.length - 1];
};

export const contextReasonCodeLabelPath = (context: ContextSchema): string[] =>
  context.content.payload.reasonCodes.map(({ label }: ReasonCode) => label);

export const existingChoices = (activeContext: ContextSchema[]): string[] =>
  activeContext?.length > 0
    ? contextReasonCodeLabelPath(_.maxBy(activeContext, (ctx: ContextSchema) => ctx.timeCreated)!)
    : [];

export const getLabel = (reasonCodes: ReasonCode[], choices: string[]): string | undefined => {
  if (choices.length === 0) {
    return reasonCodes[0].prompt;
  } else {
    const children = reasonCodes.find((rc) => rc.label === choices[0])?.children;
    if (children) {
      return getLabel(children, choices.slice(1));
    }
  }
};

export const getOptions = (reasonCodes: ReasonCode[] | undefined, choices: string[]): ReasonCode[] | undefined => {
  if (!reasonCodes) {
    return [];
  } else if (choices.length === 0) {
    return reasonCodes.map((rc): ReasonCode => ({ label: rc.label }));
  } else if (choices.length > 0) {
    return getOptions(reasonCodes.find((rc) => rc.label === choices[0])?.children, choices.slice(1));
  }
};

/** When there's only one option, this function adds it to the list of choices. */
export const inferChoices = (reasonCodes: ReasonCode[] | undefined, choices: string[]): string[] => {
  const options = getOptions(reasonCodes, choices) ?? [];
  if (options.length === 1 && options[0].label) {
    return inferChoices(reasonCodes, [...choices, options[0].label]);
  }
  return choices;
};

export const hierarchyLabel = (labels: string[] | null, index: number) => {
  return labels?.[index] || t('VANTAGE.OTHER.REASON_CODES.CATEGORY', { name: index + 1 });
};

export const newChoices = (choices: string[], index: number, value: string | null) => {
  if (value) {
    choices = choices.slice(0, index + 1);
    choices[index] = value;
    return choices;
  } else {
    return choices.slice(0, index);
  }
};

export const labelingWindowValidator = ({
  labelingWindowHours,
}: ReasonCodesConfig): ((nanoseconds: number) => boolean) => {
  if (labelingWindowHours) {
    const maxNanosAgo = labelingWindowHours * 60 * 60 * 1_000_000_000;
    const nowNanos = new Date().getTime() * 1_000_000;
    return (nanoseconds: number) => nowNanos - nanoseconds > maxNanosAgo;
  } else {
    return (nanoseconds: number) => true;
  }
};

export const findReasonCode = (reasonCodes: ReasonCode[], labelPath: string[]) => {
  let reasonCode: ReasonCode | undefined = { children: reasonCodes };
  let labelName: string;
  while (labelPath.length > 0) {
    [labelName, ...labelPath] = labelPath;
    reasonCode = reasonCode?.children?.find((rc) => rc.label === labelName);
  }
  return reasonCode;
};

export const reasonCodesForConditions = async (tableId: string, conditionIds: string[]): Promise<ReasonCode[][]> => {
  const { data: rows } = await graphql.getTable(tableId, {
    limit: 1000,
    columns: ['Reason codes'],
    filter: {
      valueFilter: {
        columnName: SeeqNames.MaterializedTables.ItemIdColumn,
        filterType: 'IN',
        value: conditionIds,
      },
    },
  });
  return rows.map((row: Record<string, string>) => JSON.parse(row['Reason codes']));
};

export const duration = (start: string | Date | moment.Moment, end: string | Date | moment.Moment) =>
  moment.duration(moment(end).diff(moment(start)));

export const capsuleByContext = (ctx: ContextSchema) => {
  const { reasonCodes } = ctx.content.payload;
  const eventStart = ctx.content.payload?.eventStart ?? ctx.eventStart;
  const eventEnd = ctx.content.payload?.eventEnd ?? ctx.eventEnd;

  return {
    conditionId: ctx.conditionId,
    datumId: ctx.kindId,
    duration: duration(eventStart!, eventEnd!),
    reasonCodes,
  };
};

export const capsuleByRow = (row: SimplifiedNode, reasonCodesContexts: ContextSchema[]) => {
  const { Start, End } = VantageNames.Columns;
  const datumId = row.selectedRow[VantageNames.Columns.DatumID];
  const reasonCodes =
    reasonCodesContexts.find((context) => context.kindId === datumId)?.content.payload.reasonCodes || [];

  return {
    conditionId: row.selectedRow[VantageNames.Columns.ConditionID],
    datumId,
    duration: duration(row.selectedRow[Start], row.selectedRow[End]),
    reasonCodes,
  };
};

export const calculateAllocatedTime = (capsuleTimeSlots: SelectedCapsules[]): moment.Duration => {
  return capsuleTimeSlots.reduce((sum, slot) => sum.clone().add(slot.duration), moment.duration(0));
};

export const calculateRemainingTime = (
  selectedRowTotalDuration: moment.Duration,
  allocatedTime: moment.Duration,
): moment.Duration => {
  return selectedRowTotalDuration.clone().subtract(allocatedTime);
};

const reasonCodeStagedContext = (timeSlot: SelectedCapsules, windows: ReasonCodeWindows) => ({
  identifier: VantageNames.Extensions.ReasonCodes,
  content: {
    value: timeSlot.reasonCodes.map((rc) => rc.label).join(', '),
    payload: {
      reasonCodes: timeSlot.reasonCodes,
      windows,
      datumId: timeSlot.datumId,
    },
  },
});

/**
 * Creates a new array of staged contexts based on capsule time slots and active contexts.
 *
 * This function serves three main purposes:
 * 1. Multiple capsules selection (does not support capsule splitting):
 *    - Creates a new context entry for each selected capsule
 *    - Sets start and end to the respective start and end times of the selected capsules
 *    - Stores starts and ends in the content payload by capsule datum ID for use in the Reason Codes context hook
 *    - Sets context content value from the reason codes' labels
 * 2. Single capsule selection with optional capsule splitting:
 *    - Creates new context entries for each capsule time slot
 *    - Calculates start time based on cumulative duration of previous slots
 *    - Calculates end time based on cumulative duration including the current slot
 *    - Stores starts and ends in the content payload for use in the context hook, as they may differ from capsule
 *    start and end
 *    - Sets context content value from the reason codes' labels
 *
 * 3. If active contexts exist, adds an additional staged context that:
 *    - Includes all non-categorized active contexts
 *    - Contains deletion information for existing contexts
 *    - Prepares reason code deletion data with label paths and timing information
 *
 */
export const getStagedContext = (
  capsuleTimeSlots: SelectedCapsules[],
  activeContext: ContextSchema[],
  contextIds: string[],
  capsuleStartTimes: moment.Moment[],
  capsuleEndTimes: moment.Moment[],
  capsuleDatumIds: string[],
): StagedContext[] => {
  let newContexts: StagedContext[];
  if (capsuleStartTimes.length === 1) {
    // Single capsule selection, may involve more than one capsuleTimeSlot (splitting)
    newContexts = capsuleTimeSlots.map((slot, index) => {
      const start = capsuleStartTimes[0]
        .clone()
        .add(capsuleTimeSlots.slice(0, index).reduce((sum, slot) => sum.add(slot.duration), moment.duration(0)))
        .toDate();
      const end = capsuleStartTimes[0]
        .clone()
        .add(capsuleTimeSlots.slice(0, index + 1).reduce((sum, slot) => sum.add(slot.duration), moment.duration(0)))
        .toDate();
      const windows = {
        [slot.datumId]: { start, end },
      };
      return reasonCodeStagedContext(slot, windows);
    });
  } else {
    // Multiple capsule selection, does not support capsule splitting, supports one capsuleTimeSlot
    const windows: ReasonCodeWindows = {};
    const slot = capsuleTimeSlots[0];
    capsuleStartTimes.forEach((capsuleStartTime, index) => {
      const start = capsuleStartTime.toDate();
      const end = capsuleEndTimes[index].toDate();
      windows[capsuleDatumIds[index]] = { start, end };
    });
    newContexts = [reasonCodeStagedContext(slot, windows)];
  }

  if (activeContext.length > 0) {
    newContexts.push({
      identifier: VantageNames.Extensions.ReasonCodes,
      content: {
        value: activeContext
          .filter((ctx) => ctx.category === VantageNames.ReasonCodesLabelCategory)
          .map((ctx) => ctx.content.value)
          .join(', '),
      },
      deletionIds: contextIds,
      reasonCodeDeletions: activeContext.map((ctx) => ({
        labelPath: contextReasonCodeLabelPath(ctx),
        contextId: ctx.id,
        startTime: ctx.eventStart!,
        endTime: ctx.eventEnd!,
      })),
    });
  }

  return newContexts;
};

/**
 * Creates a single staged context entry specifically for existing user choices.
 *
 * This function differs from getStagedContext in that it focuses specifically on handling existing choices:
 * - Creates a single staged context that combines all non-categorized contexts into one value string
 * - References an "existingChoices" path based on active context
 * - Includes deletion IDs for cleanup
 * - Prepares reason code deletions specifically for categorized contexts
 * - Ensures uniqueness in the deletion list
 */
export const getStagedContextForExistingChoices = (
  activeContext: ContextSchema[],
  contextIds: string[],
): StagedContext[] => {
  return [
    {
      identifier: VantageNames.Extensions.ReasonCodes,
      content: {
        value: activeContext
          .filter((ctx) => ctx.category === VantageNames.ReasonCodesLabelCategory)
          .map((ctx) => ctx.content.value)
          .join(', '),
        reasonCodePath: existingChoices(activeContext),
      },
      deletionIds: contextIds,
      reasonCodeDeletions: _.chain(activeContext)
        .filter(({ category }) => category !== VantageNames.ReasonCodesLabelCategory)
        .map((ctx) => ({
          labelPath: contextReasonCodeLabelPath(ctx),
          contextId: ctx.id,
          startTime: ctx.eventStart!,
          endTime: ctx.eventEnd!,
        }))
        .uniq()
        .value(),
    },
  ];
};

export const checkRowsContainSplits = (
  selectedRows: SimplifiedNode[],
  reasonCodesContexts: ContextSchema[],
): boolean => {
  return selectedRows.some((row) => {
    const datumId = row.selectedRow[VantageNames.Columns.DatumID];
    const contexts = _.groupBy(reasonCodesContexts, 'kindId')[datumId];
    return contexts && contexts.length >= 2;
  });
};
